import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { franquiaService } from '../../services/franquiaService';
import { Franquia, FranquiaInput } from '../../types/franquia';

interface FranquiaFormProps {
  franquia?: Franquia;
  isEditing?: boolean;
  franquiaId?: number;
}

export const FranquiaForm: React.FC<FranquiaFormProps> = ({ 
  franquia, 
  isEditing = false, 
  franquiaId 
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FranquiaInput>({
    razao_social: franquia?.razao_social || '',
    nome_fantasia: franquia?.nome_fantasia || '',
    cnpj: franquia?.cnpj || '',
    limite_funcionarios: franquia?.limite_funcionarios || 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (franquia) {
      setFormData({
        razao_social: franquia.razao_social,
        nome_fantasia: franquia.nome_fantasia,
        cnpj: franquia.cnpj,
        limite_funcionarios: franquia.limite_funcionarios,
      });
    }
  }, [franquia]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing && franquiaId) {
        await franquiaService.atualizar(franquiaId, formData);
        navigate(`/franquias/${franquiaId}/detalhes`);
      } else {
        await franquiaService.criar(formData);
        navigate('/franquias');
      }
    } catch (err: any) {
      console.error('Erro ao salvar franquia:', err);
      setError(err.response?.data?.message || 'Erro ao salvar franquia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="mt-5 md:mt-0 md:col-span-3">
          <form onSubmit={handleSubmit}>
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">{error}</h3>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="nome_fantasia" className="block text-sm font-medium text-[#29306a]">
                    Nome Fantasia *
                  </label>
                  <input
                    type="text"
                    name="nome_fantasia"
                    id="nome_fantasia"
                    required
                    maxLength={255}
                    value={formData.nome_fantasia}
                    onChange={handleInputChange}
                    className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label htmlFor="razao_social" className="block text-sm font-medium text-[#29306a]">
                    Razão Social *
                  </label>
                  <input
                    type="text"
                    name="razao_social"
                    id="razao_social"
                    required
                    maxLength={255}
                    value={formData.razao_social}
                    onChange={handleInputChange}
                    className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label htmlFor="cnpj" className="block text-sm font-medium text-[#29306a]">
                    CNPJ *
                  </label>
                  <input
                    type="text"
                    name="cnpj"
                    id="cnpj"
                    required
                    maxLength={18} // ##.###.###/####-##
                    value={formData.cnpj}
                    onChange={handleInputChange}
                    className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label htmlFor="limite_funcionarios" className="block text-sm font-medium text-[#29306a]">
                    Limite de Funcionários *
                  </label>
                  <input
                    type="number"
                    name="limite_funcionarios"
                    id="limite_funcionarios"
                    required
                    value={formData.limite_funcionarios}
                    onChange={handleInputChange}
                    className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>

              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="button"
                  onClick={() => isEditing ? navigate(`/franquias/${franquiaId}/detalhes`) : navigate('/franquias')}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-[#29306a] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0] mr-3"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#32bef0] hover:bg-[#2ba8d8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0] disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};